<script setup lang="ts">
import { inject,ref,watch,onMounted,Ref } from 'vue'
import { getLoginMethod, useFaceIDAuthentication } from '@/services/User';

import {getData, setData, NewSparkData} from '@/services/idb'
import { sendGraphQLQuery } from '@/services/gql-util'
import { RouterLink, RouterView,useRoute, useRouter,RouteRecordNormalized,RouteLocationNormalized } from 'vue-router'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, } from '@/components/ui/alert-dialog'
import { useCookies } from '@vueuse/integrations/useCookies';
import { Member  } from '@/types/Member'
import FaceIDAuth from "@/components/FaceIDAuth.vue";

// Reactive variables to hold user input values
const email = ref('')
const password = ref('')
const errorMessage = ref('')

// Dialog states
const showDialog = ref(false)
const dialogMessage = ref('')
const dialogTitle = ref('')
const ssoEnabled = ref<boolean>(false);
const cookies = useCookies(['samlEmail', 'redirectUrl']); 
const userInfo = inject<Ref<Member> | null>('userInfo', null);
const router = useRouter();
const storedData = ref<NewSparkData | null>(null);
const isAuthenticated = ref(false);
let authenticateWithFaceID: () => Promise<void> = async () => {}; 

function isValidEmail(email: string): boolean {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
}

onMounted(async () => {
	const data = await getData();

	if (data && data.dbId) {
		storedData.value = data as NewSparkData; 
		const auth = useFaceIDAuthentication(storedData.value);
		isAuthenticated.value = auth.isAuthenticated.value;
		authenticateWithFaceID = auth.authenticateWithFaceID;
	}
	if(userInfo && userInfo.value) {
		if(userInfo.value.id!="1") {
			router.push('/')

		}
	}
	const samlEmail = cookies.get('samlEmail');
	if (samlEmail) {
		email.value = samlEmail;
	}
});

watch(email, async (newEmail: string) => {
	if (isValidEmail(newEmail)) {
		const baseUrl = window.location.origin;

		// Get the full URL (including path and query params)
		const fullUrl = window.location.href;

		// Check if the pathname is '/login'
		const cookieValue = window.location.pathname === '/login' ? baseUrl : fullUrl;
		cookies.set('samlEmail', newEmail, { maxAge: 30 });
		cookies.set('redirectUrl', cookieValue, { maxAge: 300 });
		const response = await getLoginMethod(newEmail);
		if(response.id!=0) {
			const tmp = await setData({dbId:response.id,faceId:0});
			ssoEnabled.value = true;
			window.location.href = '/saml';
		}

	}
});


// Method to confirm credentials and handle login
const confirmCredentials = async (event: Event) => {
	event.preventDefault()  // Prevent the form from submitting in the traditional way

	// Perform form validation (optional but recommended)
	if (!email.value || !password.value) {
		dialogMessage.value = "Please fill in both email and password."
		dialogTitle.value = "Error"
		showDialog.value = true
		return
	}

	try {
		const query = `
		query ($vhost: Int!, $username: String!, $password: String!, $returnUserInfo: Boolean, $login: Boolean, $isEmail: Boolean) {
			Users {
				confirmCredentials(vhost: $vhost, username: $username, password: $password, returnUserInfo: $returnUserInfo, login: $login, isEmail: $isEmail)
			}
		}`;
		// First attempt with username
		let result: any = null;
		try {
			const data = await sendGraphQLQuery<{ Users: { confirmCredentials: any } }>(query, {
				vhost: 2,
				username: email.value,
				password: password.value,
				returnUserInfo: true,
				login: true,
				isEmail: false,
			});
			result = data?.Users?.confirmCredentials;
		} catch {
			result = null;
		}

		if (!result || (result.status === false && result !== false)) {
			// Retry with isEmail flag
			try {
				const data = await sendGraphQLQuery<{ Users: { confirmCredentials: any } }>(query, {
					vhost: 2,
					username: email.value,
					password: password.value,
					returnUserInfo: true,
					login: true,
					isEmail: true,
				});
				result = data?.Users?.confirmCredentials;
			} catch {
				result = null;
			}
		}

		if (result && result.id && userInfo) {
			const tmp = await setData({dbId: result.id, faceId: 1});
			userInfo.value = result;
			router.push('/');
		} else {
			dialogTitle.value = "Login Failed";
			dialogMessage.value = (typeof result === 'string') ? result : "Invalid credentials";
			showDialog.value = true;
		}
	} catch (error) {
		console.error('Error during login:', error)
		dialogMessage.value = "An error occurred while logging in."
		dialogTitle.value = "Error"
		showDialog.value = true
	}
}
</script>

<template>
	<div class="flex flex-col justify-center items-center w-full h-screen " >
		<div class="flex justify-center w-full">
			<div class="flex justify-center border border-slate-300 w-full md:w-75 m-4 lg:m-0">
				<div class="p-0 hidden lg:block">
					<img src="https://assets.newspark.ca/2/v7/gfx/ugcmanager.png" class="h-full" />
				</div>
				<div class="flex w-full lg:w-3/4 justify-center items-center bg-background space-y-8 p-4 lg:p-2">
					<div class="w-full p-8  md:px-32 lg:px-24">
						<h1 class="text-gray-800 font-light text-2xl mb-1">New Spark UGC Manager!</h1>
						<div class="flex items-center border-2 mb-8 py-2 px-3 rounded-2xl">
							<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
							</svg>
							<input v-model="email" id="email" class="pl-2 w-full outline-hidden border-none" type="email" name="email" placeholder="Email Address" />
						</div>
						<div class="flex items-center border-2 mb-12 py-2 px-3 rounded-2xl">
							<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
								<path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
							</svg>
							<input v-model="password" class="pl-2 w-full outline-hidden border-none" type="password" name="password" id="password" placeholder="Password" />
						</div>
						<Alert v-if="errorMessage!=''">
						    <AlertTitle>Login Error!</AlertTitle>
						        <AlertDescription>
							{{errorMessage}}.
							          </AlertDescription>
								    </Alert>
						<div class="w-full flex justify-center items-center">
							<div class="w-full">
								<button @click.prevent="confirmCredentials" type="submit" class="block w-full bg-blue-600 mt-5 py-2 rounded hover:bg-blue-700 hover:-translate-y-1 transition-all duration-500 text-white font-semibold mb-2">
									Login
								</button>
							</div>
							<div v-if="storedData && storedData.faceId">

								<FaceIDAuth :userId="storedData.dbId" actionType="authenticate"/>
							</div>
						</div>
						<div class="flex flex-col lg:flex-row justify-between mt-4">
							<div>
								<span class="text-sm ml-2 hover:text-blue-500 cursor-pointer hover:-translate-y-1 duration-500 transition-all">Forgot Password?</span>
							</div>
							<div>
								<a href="#" class="text-sm ml-2 hover:text-blue-500 cursor-pointer hover:-translate-y-1 duration-500 transition-all">Don't have an account yet?</a>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- ShadCN Alert Dialog -->
	<AlertDialog v-model:open="showDialog">
	<AlertDialogTrigger />
	<AlertDialogContent>
	<AlertDialogHeader>{{ dialogTitle }}</AlertDialogHeader>
	<p class="text-gray-700">{{ dialogMessage }}</p>
	<AlertDialogFooter>
	<AlertDialogCancel @click="showDialog = false">Close</AlertDialogCancel>
	</AlertDialogFooter>
	</AlertDialogContent>
	</AlertDialog>
</template>

<style >
/* Ensure the parent div takes up the full screen height */
body, html {
	margin: 0;
	padding: 0;
	height: 100vh; /* Full viewport height */
	display: flex; /* Optional: if you want to center content */
	justify-content: center; /* Optional: horizontally center content */
	align-items: center; /* Optional: vertically center content */

	/* Background settings */
	background-image: url('https://assets.newspark.ca/2/v7/gfx/newspark2025bg.png'); /* Replace with your image path */
	background-size: cover; /* Scale image to cover the entire body */
	background-repeat: no-repeat; /* Prevent tiling */
	background-position: center; /* Center the image */
}


.flex {
	display: flex;
}

.justify-center {
	justify-content: center;
}

.items-center {
	align-items: center;
}

.w-full {
	width: 100%;
}

.h-screen {
	height: 100vh;
}

.space-y-8 > *:not(:last-child) {
	margin-bottom: 2rem;
}
[v-cloak] {
	display: none;
}
</style>

